import React, { useMemo } from 'react';

/**
 * FiveDimensionRadar — SVG pentagon radar for the 5 trust dimensions.
 *
 * The 5 dimensions (Compliance / Volume / Tenure / Severity / Consistency)
 * are derived by the backend scoring engine and constitute the breakdown
 * of an agent's overall trust score. The radar lets an operator see at
 * a glance WHICH dimension is dragging the score down — e.g. "score is
 * 72 because severity is 40, everything else is 90+".
 *
 * All values normalised 0-100. Rendered as a filled polygon over a
 * 5-ring grid, with axis labels positioned just outside each vertex.
 *
 * Phase A4 / G304 — used in InventoryTab row-expanded detail.
 *
 * @param {Object} props
 * @param {Object} props.dimensions   — { compliance, volume, tenure, severity, consistency }
 * @param {number} [props.size=240]
 */
export function FiveDimensionRadar({ dimensions = {}, size = 240 }) {
  const labels = useMemo(() => ([
    { key: 'compliance',  label: 'Compliance',  value: clamp(dimensions.compliance) },
    { key: 'volume',      label: 'Volume',      value: clamp(dimensions.volume) },
    { key: 'tenure',      label: 'Tenure',      value: clamp(dimensions.tenure) },
    { key: 'severity',    label: 'Severity',    value: clamp(dimensions.severity) },
    { key: 'consistency', label: 'Consistency', value: clamp(dimensions.consistency) },
  ]), [dimensions]);

  // Geometry — pentagon with vertex 0 at the top.
  const cx = size / 2;
  const cy = size / 2;
  const r = size * 0.34;   // outer radius (100% ring)
  const labelR = r + size * 0.11;

  // Horizontal/vertical padding so the axis labels (which sit OUTSIDE the
  // pentagon and are centre-anchored on each spoke) never clip against the
  // viewBox edge. The square chart lives in [0,size]; the labels can extend
  // ~size*0.26 beyond the left/right vertices, so the viewBox is widened
  // symmetrically and the SVG renders at the padded width.
  const padX = Math.round(size * 0.26);
  const padY = Math.round(size * 0.12);
  const vbW = size + padX * 2;
  const vbH = size + padY * 2;

  const vertices = useMemo(() => labels.map((_, i) => {
    const angle = (-Math.PI / 2) + (2 * Math.PI * i) / labels.length;
    return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle), angle };
  }), [labels.length, cx, cy, r]);

  /* Compute the data polygon — each vertex sits at (value/100) along
   * the spoke from the centre. We round to 2dp so the rendered SVG is
   * stable across re-renders (no React diff noise). */
  const polygonPoints = labels.map((d, i) => {
    const v = d.value / 100;
    const x = cx + r * v * Math.cos(vertices[i].angle);
    const y = cy + r * v * Math.sin(vertices[i].angle);
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(' ');

  // Concentric guide rings at 25/50/75/100%.
  const ringPoints = (pct) => labels.map((_, i) => {
    const x = cx + r * pct * Math.cos(vertices[i].angle);
    const y = cy + r * pct * Math.sin(vertices[i].angle);
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(' ');

  const avg = Math.round(labels.reduce((s, d) => s + d.value, 0) / labels.length);

  return (
    <div className="inline-flex flex-col items-center">
      <svg width={vbW} height={vbH} viewBox={`${-padX} ${-padY} ${vbW} ${vbH}`} role="img"
           aria-label={`Five-dimension trust radar: ${labels.map((d) => `${d.label} ${d.value}`).join(', ')}`}>
        {/* Guide rings */}
        {[0.25, 0.5, 0.75, 1].map((pct) => (
          <polygon key={pct}
            points={ringPoints(pct)}
            fill="none"
            stroke="var(--v2-border-subtle)"
            strokeWidth={0.75}
          />
        ))}
        {/* Spokes */}
        {vertices.map((v, i) => (
          <line key={i}
            x1={cx} y1={cy} x2={v.x} y2={v.y}
            stroke="var(--v2-border-subtle)"
            strokeWidth={0.75}
          />
        ))}
        {/* Data polygon — fill */}
        <polygon
          points={polygonPoints}
          fill="var(--v2-action-soft)"
          fillOpacity={0.7}
          stroke="var(--v2-action)"
          strokeWidth={1.5}
          strokeLinejoin="round"
        />
        {/* Data points */}
        {labels.map((d, i) => {
          const v = d.value / 100;
          const x = cx + r * v * Math.cos(vertices[i].angle);
          const y = cy + r * v * Math.sin(vertices[i].angle);
          return (
            <circle key={d.key} cx={x} cy={y} r={3}
                    fill="var(--v2-bg-surface)" stroke="var(--v2-action)" strokeWidth={1.5} />
          );
        })}
        {/* Axis labels — the word and its number are both centre-anchored
            on the spoke so they stack and align to each other (and to the
            vertex). The viewBox padding above guarantees neither clips. */}
        {labels.map((d, i) => {
          const angle = vertices[i].angle;
          const x = cx + labelR * Math.cos(angle);
          const y = cy + labelR * Math.sin(angle);
          // Top vertex: lift both lines above the point. Bottom/side
          // vertices: drop them below so the word never overlaps the ring.
          const isTop = Math.sin(angle) < -0.6;
          const wordDy = isTop ? -8 : 0;
          const numDy = isTop ? 4 : 12;
          return (
            <g key={d.key}>
              <text x={x} y={y + wordDy} textAnchor="middle"
                    fontSize={10.5} fontWeight={600}
                    fill="var(--v2-ink)" >
                {d.label}
              </text>
              <text x={x} y={y + numDy} textAnchor="middle"
                    fontSize={10}
                    fill="var(--v2-ink-muted)" fontFamily="monospace">
                {d.value}
              </text>
            </g>
          );
        })}
        {/* Centre score */}
        <circle cx={cx} cy={cy} r={size * 0.07} fill="var(--v2-bg-surface)" />
        <text x={cx} y={cy - 2} textAnchor="middle"
              fontSize={size * 0.06} fontWeight={700} fill="var(--v2-ink)">
          {avg}
        </text>
        <text x={cx} y={cy + size * 0.04} textAnchor="middle"
              fontSize={size * 0.035} fill="var(--v2-ink-muted)" letterSpacing="0.08em">
          AVG
        </text>
      </svg>
    </div>
  );
}

function clamp(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.max(0, Math.min(100, x));
}

export default FiveDimensionRadar;
